import { DefaultCFRegex, type FamilyFormPropsType, type FileType } from 'app/src/model'
import { membershipApi, relationShipKeys, type Relationship, type UserFamilyMember } from 'app/src/services/welfareportal'
import { ErrorMessage, FileUploader, PortalDialog } from 'app/src/shared/components'
import { borderLessClassNames, getFromSessionStorage, queryClient, validateFiscalCode } from 'app/src/shared/helpers'
import { useHttpClient, useWebAppContext } from 'app/src/shared/hooks'
import { useFormik } from 'formik'
import { isEqual } from 'lodash'
import { Checkbox } from 'primereact/checkbox'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import type { SelectItemOptionsType } from 'primereact/selectitem'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { boolean, mixed, object, string } from 'yup'

export default function FamilyForm(props: FamilyFormPropsType) {
  const { t } = useTranslation('userInfo', { keyPrefix: 'UserInfo.FamilyForm' })
  const membership = getFromSessionStorage('selectedCampaign') ?? '{}'
  const membershipId = JSON.parse(membership).membershipId
  const relationshipsT = useTranslation('userInfo', { keyPrefix: 'UserInfo.FamilyRelationships' })
  const globalTranslation = useTranslation('home', { keyPrefix: 'Global' })

  const [checked, setChecked] = useState<boolean>(false)
  const genErrRef = useRef<HTMLDivElement | null>(null)
  const bodyRef = useRef<HTMLDivElement | null>(null)
  const firstInputRef = useRef<HTMLInputElement | null>(null)

  const [generalError, setGeneralError] = useState<string>()
  const [requestSubmit, setRequestSubmit] = useState<boolean>(false)
  const [touchErrorEnabled, setTouchErrorEnabled] = useState<boolean>(false)
  const [showRelationshipNote, setShowRelationshipNote] = useState<boolean>(false)

  const context = useWebAppContext()
  const [privacyFile, setPrivacyFile] = useState<FileType>()
  const [showPrivacyFileSection, setShowPrivacyFileSection] = useState<boolean>(false)

  const httpClient = useHttpClient()
  const onPrivacyFileStateChange = useCallback((file?: FileType) => {
    setPrivacyFile(file)
  }, [])
  const isDependentOfOptions: SelectItemOptionsType = useMemo(
    () => [
      { value: true, label: globalTranslation.t('Yes'), key: 'true' },
      { value: false, label: globalTranslation.t('No'), key: 'false' },
    ],
    [globalTranslation],
  )

  const documentCreateMutation = membershipApi.useUserFamilyMembersDocumentUpload()
  const documentDeleteMutation = membershipApi.useUserFamilyMembersDocumentDelete()
  const notifyDocumentUploadMutation = membershipApi.useUserFamilyMembersDocumentUpdate()
  const createFamilyMemberMutation = membershipApi.useUserFamilyMembersCreate({
    onSuccess: () => {
      membershipApi.invalidateCacheByOperationId(queryClient, 'membership-api.membershipGet')
    },
    onError: (err) => {
      context.logger?.error('Error Creating Family Member', { err })
      if (err.status === 400) setGeneralError(t('SaveErrors.BadRequest'))
      else setGeneralError(t('SaveErrors.AddGenericError'))
    },
  })
  const updateFamilyMemberMutation = membershipApi.useUserFamilyMembersUpdate({
    onSuccess: () => {
      membershipApi.invalidateCacheByOperationId(queryClient, 'membership-api.membershipGet')
    },
    onError: (err) => {
      context.logger?.error('Error Updating Family Member', { err })
      if (err.status === 400) setGeneralError(t('SaveErrors.BadRequest'))
      else setGeneralError(t('SaveErrors.UpdateGenericError'))
    },
  })

  const validateCfLocal = useCallback((fiscalCode: string, name?: string, surname?: string) => {
    // Use local validation if we have first and last name, otherwise fallback to API
    return validateFiscalCode(fiscalCode, name, surname)
  }, [])

  useEffect(() => {
    if (!showPrivacyFileSection) onPrivacyFileStateChange(undefined)
  }, [onPrivacyFileStateChange, showPrivacyFileSection])

  useEffect(() => {
    const effectWorker = () => {
      if (!context.userInfo.welfareCampaign.configuration.familyMemberPrivacyDocument) return
      let showPrivacySection = false

      if (props.familyMember) {
        if (props.familyMember?.privacyDocument && props.familyMember.privacyDocument.status === 'uploaded') {
          console.log('aasdsadsada')
          showPrivacySection = true
          setPrivacyFile({
            documentName: props.familyMember.privacyDocument.documentName,
            documentId: props.familyMember.privacyDocument.documentId,
            documentUri: props.familyMember.privacyDocument.urlPath,
          })
        } else {
          const validateResponse = validateCfLocal(
            props.familyMember.fiscalCode,
            props.familyMember.firstName,
            props.familyMember.lastName,
          )
          showPrivacySection =
            validateResponse.isValid && typeof validateResponse.isAdult !== 'undefined' ? validateResponse.isAdult : false
        }
        setShowPrivacyFileSection(showPrivacySection)
      }
    }

    effectWorker()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.familyMember?.fiscalCode])

  const upsertSuccessCallback = useCallback(
    async (familyMembersId: string) => {
      if (context.userInfo.welfareCampaign.configuration.familyMemberPrivacyDocument) {
        //Privacy File Handling
        if (privacyFile?.blob) {
          try {
            // 1- Create Document Id
            const documentCreateResponse = await documentCreateMutation.mutateAsync({
              body: { contentType: privacyFile.documentMimeType!, documentName: privacyFile.documentName },
              pathParams: { familyMembersId },
            })

            // 2- Upload Document File
            const uploadFileResponse = await httpClient.uploadFile(documentCreateResponse.url, privacyFile.blob)

            if (uploadFileResponse.fetchError) {
              context.logger?.error('Error Uploading Family Member Privacy File', { error: uploadFileResponse.fetchError })
              throw new Error()
            }

            // 3 - Notify Document Upload
            await notifyDocumentUploadMutation.mutateAsync({
              body: { documentName: privacyFile.documentName!, documentStatus: 'uploaded' },
              pathParams: { documentId: documentCreateResponse.documentId!, familyMembersId },
            })
          } catch (er) {
            context.logger?.error('Error Uploading Family Member Privacy File', { error: er })
            context.showToastMessage({ content: t('SaveErrors.PrivacyFileUploadError'), severity: 'error' })
          }
        } else {
          // Delete Document File
          if (props.familyMember?.privacyDocument && !privacyFile) {
            try {
              await documentDeleteMutation.mutateAsync({
                pathParams: {
                  documentId: props.familyMember.privacyDocument.documentId,
                  familyMembersId: props.familyMember.familyMemberId,
                },
              })
            } catch {
              context.logger?.error('Error Deleting Family Member Privacy File')
            }
          }
        }
      }
      membershipApi.invalidateCacheByOperationId(queryClient, 'membership-api.userMembershipActiveGet')
      props.callbackOnFamilyFormHide()
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [context, documentCreateMutation, httpClient, notifyDocumentUploadMutation, privacyFile, props, t],
  )

  useEffect(() => {
    if (generalError && bodyRef.current) {
      bodyRef.current?.scrollIntoView()
      setTimeout(() => {
        setGeneralError(undefined)
      }, 3000)
    }
  }, [generalError])

  const inputTextStyle = borderLessClassNames

  const yupSchema = useMemo(() => {
    const fiscalCodeList = props.membership?.membershipInfo.userFamilyMembers?.map((x) => x.fiscalCode)
    return object().shape({
      firstName: string()
        .transform((value) => value?.trim())
        .min(2, t('ValidationMessages.Min.FirstName', { CHARCOUNT: '2' }))
        .matches(
          /^(?!.*[ \-'][ \-'])(?:(?:[A-Za-zÀ-ÖØ-öø-ÿ][-']?)+ ?)+[A-Za-zÀ-ÖØ-öø-ÿ]'?$/g,
          t('ValidationMessages.Format.FirstName'),
        )
        .max(60, t('ValidationMessages.Max.FirstName', { CHARCOUNT: '60' }))
        .required(t('ValidationMessages.Required.FirstName')),
      lastName: string()
        .transform((value) => value?.trim())
        .min(2, t('ValidationMessages.Min.LastName', { CHARCOUNT: '2' }))
        .matches(
          /^(?!.*[ \-'][ \-'])(?:(?:[A-Za-zÀ-ÖØ-öø-ÿ][-']?)+ ?)+[A-Za-zÀ-ÖØ-öø-ÿ]'?$/g,
          t('ValidationMessages.Format.LastName'),
        )
        .max(60, t('ValidationMessages.Max.LastName', { CHARCOUNT: '60' }))
        .required(t('ValidationMessages.Required.LastName')),
      fiscalCode: string()
        .transform((value) => value?.toUpperCase())
        .min(16, t('ValidationMessages.Min.FiscalCode', { CHARCOUNT: '16' }))
        .uppercase()
        .test({
          async test(value, yupContext) {
            const valueUpper = value?.toUpperCase() ?? ''
            if (!props.isEdit && fiscalCodeList?.some((x) => x?.toUpperCase() === valueUpper))
              return yupContext.createError({ message: t('ValidationMessages.Custom.FiscalCodeNotUnique') })

            if (props.membership?.membershipInfo.userFiscalCode?.toUpperCase() === valueUpper)
              return yupContext.createError({ message: t('ValidationMessages.Custom.FiscalCodeEqualsToRequestor') })

            if (valueUpper.length >= 16 && yupContext) {
              // For validation we can't access form values here, so just do basic CF validation
              const validateResponse = validateCfLocal(valueUpper, yupContext.parent.firstName, yupContext.parent.lastName)

              if (context.userInfo.welfareCampaign.configuration.familyMemberPrivacyDocument) {
                setShowPrivacyFileSection(
                  validateResponse.isValid && typeof validateResponse.isAdult !== 'undefined' ? validateResponse.isAdult : false,
                )
              } else {
                setShowPrivacyFileSection(false)
              }

              if (!validateResponse.isValid) return yupContext.createError({ message: t('ValidationMessages.Format.FiscalCode') })
            } else setShowPrivacyFileSection(false)

            return true
          },
        })
        .matches(new RegExp(DefaultCFRegex, 'gm'), t('ValidationMessages.Format.FiscalCode'))
        .required(t('ValidationMessages.Required.FiscalCode')),
      relationship: mixed<Relationship>().required(t('ValidationMessages.Required.Relationship')),
      taxDependent: boolean().required(t('ValidationMessages.Required.TaxDependent')),
    })
  }, [
    props.isEdit,
    props.membership?.membershipInfo.userFamilyMembers,
    props.membership?.membershipInfo.userFiscalCode,
    t,
    validateCfLocal,
    context.userInfo.welfareCampaign.configuration.familyMemberPrivacyDocument,
  ])

  const formik = useFormik<Partial<UserFamilyMember>>({
    initialValues: props.familyMember ?? {
      familyMemberId: undefined,
      fiscalCode: undefined,
      firstName: undefined,
      lastName: undefined,
      relationship: undefined,
      signature: undefined,
      taxDependent: undefined,
    },
    validateOnBlur: false,
    validationSchema: yupSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        if (props.isEdit) await updateFamilyMember(values)
        else await createFamilyMember(values)
      } finally {
        setSubmitting(false)
      }
    },
  })

  useEffect(() => {
    // Check if any relationship label contains asterisk in the configured relations
    const availableRelations = relationShipKeys
      .filter((r) => context.userInfo.welfareCampaign.configuration.familyMembersRelations.includes(r))
      .map((rel) => relationshipsT.t(rel)?.label || '')

    const hasAsterisk = availableRelations.some((label) => label.includes('*'))
    setShowRelationshipNote(hasAsterisk)
  }, [relationshipsT, context.userInfo.welfareCampaign.configuration.familyMembersRelations])

  useEffect(() => {
    if (!formik.isSubmitting) setRequestSubmit(false)
  }, [formik.isSubmitting])

  const createFamilyMember = useCallback(
    async (values: Partial<UserFamilyMember>) => {
      const mutateResp = await createFamilyMemberMutation.mutateAsync({
        pathParams: {
          membershipId: membershipId,
        },
        body: {
          ...values,
          familyMemberId: '',
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          firstName: values?.firstName?.trim().toUpperCase(),
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          lastName: values?.lastName?.trim().toUpperCase(),
        },
      })

      await upsertSuccessCallback(mutateResp.familyMemberId)
    },
    [createFamilyMemberMutation, membershipId, upsertSuccessCallback],
  )

  const updateFamilyMember = useCallback(
    async (values: Partial<UserFamilyMember>) => {
      //No changes Check
      const isEqualValue = isEqual(values, props.familyMember)
      if (
        isEqualValue &&
        !privacyFile?.blob &&
        ((privacyFile && props.familyMember?.privacyDocument) || (!privacyFile && !props.familyMember?.privacyDocument))
      ) {
        setGeneralError(t('SaveErrors.NoDataModified'))
        return
      }

      if (!isEqualValue) {
        await updateFamilyMemberMutation.mutateAsync({
          pathParams: {
            // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
            familyMembersId: props.familyMember?.familyMemberId!,
          },
          body: {
            ...props.familyMember,
            ...values,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            firstName: values?.firstName?.trim().toUpperCase(),
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            lastName: values?.lastName?.trim().toUpperCase(),
          },
        })
      }

      await upsertSuccessCallback(props.familyMember?.familyMemberId ?? '')
    },
    [props.familyMember, privacyFile, upsertSuccessCallback, t, updateFamilyMemberMutation],
  )

  useEffect(() => {
    if (formik && requestSubmit) {
      console.log('Submitting form with values: ', formik.errors, formik.values)
      formik.handleSubmit()
      if (Object.keys(formik.errors)?.length > 0) setGeneralError(t('SaveErrors.BadRequest'))
      setTouchErrorEnabled(true)
    }
    // evitare Loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestSubmit, formik.errors])

  function renderAwarenessBullets() {
    const rows: JSX.Element[] = []

    if (formik?.values?.relationship === 'genitore') {
      rows.push(<li key='bullet-parent'>&bull; {t('Body.Form.ParentBullet')}</li>)
    } else {
      t('Body.Form.AwarenessBullets').map((bullet, index) => {
        rows.push(<li key={`bullet-${index}`}>&bull; {bullet}</li>)
      })
    }

    return <ul className='pl-[40px] pt-[5px]'>{rows}</ul>
  }

  return (
    <div className='card justify-content-center flex'>
      <PortalDialog
        headerProps={{
          title: props.isEdit ? t('Header.Update') : t('Header.Add'),
          subTitle: props.isEdit ? t('Header.UpdateMessageText') : t('Header.AddMessageText'),
          generalError,
          generalErrorRef: genErrRef,
        }}
        onHide={props.callbackOnFamilyFormHide}
        footerProps={{
          cancelCallback: props.callbackOnFamilyFormHide,
          cancelLabel: t('Footer.Cancel'),
          okCallBack: () => setRequestSubmit(true),
          okLabel: t('Footer.Save'),
          okDisabled: requestSubmit || !checked,
        }}
        firstInputRef={firstInputRef}
      >
        <div className='grid grid-cols-1 md:grid-cols-2' ref={bodyRef}>
          <div className='col-start-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
            <label htmlFor='firstName' className='mb-[10px] py-[5px]'>
              {t('Body.Form.NameLabel')?.toUpperCase()}
            </label>
            <InputText
              className={`${inputTextStyle} capitalize`}
              id='firstName'
              value={formik.values?.firstName}
              ref={firstInputRef}
              aria-describedby='firstName'
              placeholder={t('Body.Form.InputTextPlaceholder')}
              invalid={touchErrorEnabled && typeof formik.errors?.firstName !== 'undefined'}
              onChange={formik.handleChange}
              onFocus={() => formik.setFieldTouched('firstName')}
              onBlur={formik.handleBlur}
              maxLength={100}
            />
            <ErrorMessage errorMessage={touchErrorEnabled ? formik.errors?.firstName : undefined} />
          </div>
          <div className='col-start-1 my-[10px] grid grid-cols-1 md:col-start-2'>
            <label htmlFor='lastName' className='mb-[10px] py-[5px]'>
              {t('Body.Form.SurnameLabel')?.toUpperCase()}
            </label>
            <InputText
              className={`${inputTextStyle} capitalize`}
              id='lastName'
              value={formik.values?.lastName}
              aria-describedby='lastName'
              placeholder={t('Body.Form.InputTextPlaceholder')}
              invalid={touchErrorEnabled && typeof formik.errors?.lastName !== 'undefined'}
              onChange={formik.handleChange}
              onFocus={() => formik.setFieldTouched('lastName')}
              onBlur={formik.handleBlur}
              maxLength={100}
            />
            <ErrorMessage errorMessage={touchErrorEnabled ? formik.errors?.lastName : undefined} />
          </div>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2'>
          <div className='col-span-1 col-start-1 my-[10px] grid grid-cols-1 md:col-span-1 md:mr-[20px]'>
            <label htmlFor='fiscalCode' className='mb-[10px] py-[5px]'>
              {t('Body.Form.FiscalCodeLabel')?.toUpperCase()}
            </label>
            <InputText
              className={inputTextStyle}
              id='fiscalCode'
              value={formik.values?.fiscalCode}
              aria-describedby='fiscalCode'
              placeholder={t('Body.Form.InputTextPlaceholder')}
              invalid={touchErrorEnabled && typeof formik.errors?.fiscalCode !== 'undefined'}
              maxLength={16}
              onFocus={() => formik.setFieldTouched('fiscalCode')}
              onBlur={formik.handleBlur}
              onChange={(evt) => {
                formik.handleChange(evt)
                formik.setFieldValue('fiscalCode', evt.currentTarget.value?.toUpperCase())
              }}
            />
            <ErrorMessage errorMessage={touchErrorEnabled ? formik.errors?.fiscalCode : undefined} />
          </div>
        </div>
        <div className='grid grid-cols-1 md:grid-cols-2'>
          <div className='col-start-1 my-[10px] grid grid-cols-1 md:mr-[20px]'>
            <label htmlFor='relationship' className='mb-[10px] py-[5px]'>
              {t('Body.Form.ParentageLabel')?.toUpperCase()}
            </label>
            <Dropdown
              className={inputTextStyle}
              value={formik.values?.relationship}
              options={relationShipKeys
                .filter((r) => context.userInfo.welfareCampaign.configuration.familyMembersRelations.includes(r))
                .map((rel) => ({
                  value: rel,
                  label: relationshipsT.t(rel)?.label,
                  key: rel,
                }))}
              onChange={formik.handleChange}
              id='relationship'
              aria-describedby='relationship'
              placeholder={t('Body.Form.DropdownPlaceholder')}
              invalid={touchErrorEnabled && typeof formik.errors?.relationship !== 'undefined'}
              onFocus={() => formik.setFieldTouched('relationship')}
              onBlur={formik.handleBlur}
            />
            <ErrorMessage errorMessage={touchErrorEnabled ? formik.errors?.relationship : undefined} />
          </div>
          <div className='col-start-1 my-[10px] grid grid-cols-1 md:col-start-2'>
            <label htmlFor='taxDependent' className='mb-[10px] py-[5px]'>
              {t('Body.Form.DependentLabel')?.toUpperCase()}
            </label>
            <Dropdown
              className={inputTextStyle}
              value={formik.values?.taxDependent}
              options={isDependentOfOptions}
              onChange={formik.handleChange}
              id='taxDependent'
              aria-describedby='taxDependent'
              placeholder={t('Body.Form.DropdownPlaceholder')}
              invalid={touchErrorEnabled && typeof formik.errors?.taxDependent !== 'undefined'}
              onFocus={() => formik.setFieldTouched('taxDependent')}
              onBlur={formik.handleBlur}
            />
            <ErrorMessage errorMessage={touchErrorEnabled ? formik.errors?.taxDependent : undefined} />
          </div>
        </div>

        {showRelationshipNote && (
          <div className='my-[10px] rounded border border-blue-300 bg-blue-50 p-[15px] text-[14px] text-blue-800'>
            <p className='font-medium'>
              (*) la selezione del familiare presuppone che lo stesso sia convivente e/o percettore di assegni alimentari non
              risultanti da provvedimenti dell’autorità giudiziaria
            </p>
          </div>
        )}

        {showPrivacyFileSection && (
          <FileUploader
            title={t('Body.FileUploader.Title')}
            description={t('Body.FileUploader.Description')}
            templateBtnLabel={t('Body.FileUploader.TemplateButtonLabel')}
            templateBtnFileLink={props.membership?.membershipInfo.userFamilyMembersPrivacyTemplateURL}
            templateBtnFileName={t('Body.FileUploader.PrivacyFileName')}
            acceptExtensions={
              context.portalConfiguration?.membershipConfig?.privacyFileAcceptTypes
                ? context.portalConfiguration.membershipConfig.privacyFileAcceptTypes.join(',')
                : '.pdf,.jpg,.png'
            }
            acceptTypes={
              context.portalConfiguration?.membershipConfig?.privacyFileAcceptMimeTypes
                ? context.portalConfiguration.membershipConfig.privacyFileAcceptMimeTypes.join(',')
                : 'application/pdf,image/png,image/jpeg'
            }
            maxUploadFileSize={context.portalConfiguration.membershipConfig?.privacyFileMaxUploadSize ?? 10000000}
            supportedFilesLabel={t('Body.FileUploader.SupportedFilesLabel')}
            supportedSizeLabel={t('Body.FileUploader.SupportedSizeLabel')}
            uploadAreaLabel={t('Body.FileUploader.UploadAreaLabel')}
            uploadButtonLabel={t('Body.FileUploader.UploadButtonLabel')}
            deleteButtonLabel={t('Body.FileUploader.DeleteButtonLabel')}
            errorFileSizeNotSupported={t('Body.FileUploader.ErrorFileSizeNotSupported')}
            errorFileTypeNotSupported={t('Body.FileUploader.ErrorFileTypeNotSupported')}
            errorFileLoading={t('Body.FileUploader.ErrorFileLoading')}
            errorFileDownload={t('Body.FileUploader.ErrorFileDownload')}
            onFileStateChange={onPrivacyFileStateChange}
            file={privacyFile}
          />
        )}
        <div className='my-[20px] text-[14px] font-normal text-[#323130]'>
          <p>{t('Body.Form.AwarenessConditionText')}</p>
          {renderAwarenessBullets()}
        </div>
        <div className='flex flex-col flex-wrap items-start'>
          <div className='flex flex-row content-center items-center'>
            <Checkbox
              inputId='acceptedcond_check'
              value='acceptedcond_check'
              onChange={(e) => setChecked(e.checked!)}
              checked={checked}
            ></Checkbox>
            <label className='ml-[8px] text-[14px] font-normal text-[#323130]' htmlFor='acceptedcond_check'>
              {t('Body.AwarenessCheckboxLabel')}
            </label>
          </div>
        </div>
      </PortalDialog>
    </div>
  )
}
